import { Component, OnInit, HostListener, input, output } from '@angular/core';
import { formatDate } from '@angular/common';
import { CalendarEventType, ICalendarEvent, ICalendarItem } from '@content/calendar/calendar.component';
import { cloneDeep } from 'lodash';

interface ICalendarDay {
  day: number,
  date: string,
  isCurrentMonth: boolean,
  dayType: 'past' | 'today' | 'future',
  events: ICalendarEvent[],
  isExpanded: boolean,
}

interface CalendarData {
  calendarDays: ICalendarDay[][],
  title: string,
}

@Component({
  selector: 'app-calendar-monthly-view',
  templateUrl: './calendar-monthly-view.component.html',
  styleUrls: ['./calendar-monthly-view.component.scss'],
  standalone: false
})
export class CalendarMonthlyViewComponent implements OnInit {
  readonly calendars = input<ICalendarItem[]>([]);
  readonly selectedMonth = input<Date>(new Date());
  readonly eventClick = output<ICalendarEvent>();
  readonly eventMouseOver = output<{
    event: ICalendarEvent;
    mouse: MouseEvent;
}>();
  readonly eventMouseLeave = output<void>();

  daysOfWeek: string[] = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  data: CalendarData[] = [];
  CalendarEventType = CalendarEventType;
  calendarGridColumns = `repeat(1, 1fr)`;
  dayHeaderTitleHeight = 40;
  dayHeaderTimeHeight = 24;

  ngOnInit(): void {
    this.generateCalendar();
  }

  ngOnChanges(): void {
    this.generateCalendar();
  }

  generateCalendar(): void {
    this.calendarGridColumns = `repeat(${this.calendars().length}, 1fr)`;
    const year = this.selectedMonth().getFullYear();
    const month = this.selectedMonth().getMonth();
    const firstDayOfMonth = new Date(year, month, 1);
    const lastDayOfMonth = new Date(year, month + 1, 0);
    const startDay = firstDayOfMonth.getDay();
    const endDay = lastDayOfMonth.getDay();
    const prevMonthDays = new Date(year, month, 0).getDate();
    const today = new Date();

    // Helper function to determine dayType
    const getDayType = (date: Date): 'past' | 'today' | 'future' => {
      if (date.toDateString() === today.toDateString()) {
        return 'today';
      } else if (date < today) {
        return 'past';
      } else {
        return 'future';
      }
    };

    this.data = this.calendars().map(calendar => {
      const filteredEvents = this.getFilteredEvents(calendar);
      const weeks: ICalendarDay[][] = [];
      let week: ICalendarDay[] = [];
      // Days from the previous month
      for (let day = prevMonthDays - startDay + 1; day <= prevMonthDays; day++) {
        const date = new Date(year, month - 1, day);
        const dateString = formatDate(date, 'yyyy-MM-dd', 'en-US');
        const calendarDay: ICalendarDay = {
          day,
          date: dateString,
          isCurrentMonth: false,
          dayType: getDayType(date),
          events: this.getEventsForDay(dateString, filteredEvents),
          isExpanded: false
        };
        week.push(calendarDay);
      }
      // Days from the current month
      for (let day = 1; day <= lastDayOfMonth.getDate(); day++) {
        const date = new Date(year, month, day);
        const dateString = formatDate(date, 'yyyy-MM-dd', 'en-US');
        const calendarDay: ICalendarDay = {
          day,
          date: dateString,
          isCurrentMonth: true,
          dayType: getDayType(date),
          events: this.getEventsForDay(dateString, filteredEvents),
          isExpanded: false
        };
        week.push(calendarDay);
        if (week.length === 7) {
          weeks.push(week);
          week = [];
        }
      }
      // Days of the next month
      for (let day = 1; day <= 6 - endDay; day++) {
        const date = new Date(year, month + 1, day);
        const dateString = formatDate(date, 'yyyy-MM-dd', 'en-US');
        const calendarDay: ICalendarDay = {
          day,
          date: dateString,
          isCurrentMonth: false,
          dayType: getDayType(date),
          events: this.getEventsForDay(dateString, filteredEvents),
          isExpanded: false
        };
        week.push(calendarDay);
      }

      if (week.length) {
        weeks.push(week);
      }
      return { title: calendar.title, calendarDays: weeks}
    });
  }

  getFilteredEvents(calendar: ICalendarItem): ICalendarEvent[] {
    if (calendar.colors && calendar.colors.length > 0) {
      // Always include holiday events regardless of borough color filters
      return calendar.events.filter((event) =>
        (event.subject && event.subject.toLowerCase().includes('holiday')) || calendar.colors?.includes(event.color ?? '')
      );
    } else {
      return cloneDeep(calendar.events);
    }
  }

  getEventsForDay(date: string, events: ICalendarEvent[]): ICalendarEvent[] {

    // Filter events matching the specified date
    const eventList = Array.isArray(events)
      ? events.filter(event => {
          // Prefer using the pre-computed localStart Date if available to avoid
          // parsing timezone-shifted ISO strings; fall back to parsing start.
          const eventDate = event.localStart instanceof Date ? event.localStart : new Date(event.start);
          return formatDate(eventDate, 'yyyy-MM-dd', 'en-US') === date;
        })
      : [];
  
    // Sort by All-Day first, then by Start Time
    return eventList.sort((a, b) => {
      // Sort All-Day events first
      if (a.eventType === CalendarEventType.ALL_DAY && b.eventType !== CalendarEventType.ALL_DAY) {
        return -1; 
      }
      if (b.eventType === CalendarEventType.ALL_DAY && a.eventType !== CalendarEventType.ALL_DAY) {
        return 1; 
      }
  
      // If both events are the same type, sort by time
      return new Date(a.start).getTime() - new Date(b.start).getTime();
    });
  }  

  getButtonLabel(calendarDay: ICalendarDay): string {
    if (calendarDay.events.length <= 3) return '';
    return calendarDay.isExpanded ? 'Collapse' : `${calendarDay.events.length - 3} more items`;
  }

  onEventClick(event: ICalendarEvent): void {
    this.eventClick.emit(event);
  }

  onEventMouseOver(event: ICalendarEvent, mouse: MouseEvent): void {
    this.eventMouseOver.emit({event,mouse});
  }

  onEventMouseLeave() {
    this.eventMouseLeave.emit();
  }


  @HostListener('window:resize')
  onResize(): void {
    this.checkForScroll();
  }

  ngAfterViewInit(): void {
    this.checkForScroll(); // Initial check

    // Observe DOM changes
    const observer = new MutationObserver(() => {
      this.checkForScroll();
    });

    observer.observe(document.body, { childList: true, subtree: true });
  }

  checkForScroll(): void {
    const dayRowContainers = document.querySelectorAll('.day-row-container') as NodeListOf<HTMLElement>;
    // console.log('Day row containers:', dayRowContainers);

    dayRowContainers.forEach(container => {
      const dayRows = container.querySelectorAll('.day-row') as NodeListOf<HTMLElement>;
      // console.log('Day rows in container:', dayRows);

      dayRows.forEach(dayRow => {
        const hasVerticalScroll = container.scrollHeight > container.clientHeight;
        // console.log('Scroll Height:', container.scrollHeight);
        // console.log('Client Height:', container.clientHeight);
        // console.log('Has vertical scroll:', hasVerticalScroll);

        if (hasVerticalScroll) {
          dayRow.classList.add('has-scroll');
          // console.log('Class added: has-scroll');
        } else {
          dayRow.classList.remove('has-scroll');
          // console.log('Class removed: has-scroll');
        }
      });
    });
  }
  
  getCalendarContainerWidth() {
    const calendarContainerElement = document.querySelector('.calendar-container') as HTMLElement;
  
    if (calendarContainerElement) {
      const containerWidth = calendarContainerElement.offsetWidth; // Now offsetWidth will work
      //console.log('containerWidth', containerWidth);
      document.documentElement.style.setProperty('--calendar-container-width', `${containerWidth}px`);
    }
  }

}
